import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, AlertColor, Box, Collapse, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationEntity } from '@/types/api';

import { useNewsNotifications } from '../hooks/use-news-notifications';

type Priority = 'critical' | 'warning' | 'info';

const PRIORITY_ORDER: Priority[] = ['critical', 'warning', 'info'];

const priorityToSeverity: Record<Priority, AlertColor> = {
  critical: 'error',
  warning: 'warning',
  info: 'info',
};

const priorityEmoji: Record<Priority, string> = {
  critical: '🔴',
  warning: '⚠',
  info: 'ℹ',
};

const BannerRow = ({
  notification,
  onDismiss,
}: {
  notification: NotificationEntity;
  onDismiss: (n: NotificationEntity) => void;
}) => {
  const { t } = useTranslation();

  const handleDismiss = useCallback(() => {
    onDismiss(notification);
  }, [onDismiss, notification]);

  return (
    <Alert
      severity={priorityToSeverity[notification.priority]}
      action={
        <IconButton
          size="small"
          aria-label={t('notifications.banner.dismiss')}
          onClick={handleDismiss}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      }
      sx={{ borderRadius: 0, py: 0.5 }}
    >
      <Typography variant="body2" component="span" fontWeight={600}>
        {notification.title}
      </Typography>
      {' — '}
      <Typography variant="body2" component="span">
        {notification.summary}
      </Typography>
    </Alert>
  );
};

export const NotificationBannerPanel = () => {
  const { t } = useTranslation();
  const { bannerNotifications, isBannerCollapsed, dismissBanner, toggleCollapsed } =
    useNewsNotifications();

  // Track which priority slots are expanded to show overflow notifications
  const [expandedSlots, setExpandedSlots] = useState<Set<Priority>>(new Set());

  const toggleSlot = useCallback((priority: Priority) => {
    setExpandedSlots((prev) => {
      const next = new Set(prev);
      if (next.has(priority)) {
        next.delete(priority);
      } else {
        next.add(priority);
      }
      return next;
    });
  }, []);

  if (bannerNotifications.length === 0) return null;

  // Group notifications by priority, sorted most-recent first
  const byPriority = PRIORITY_ORDER.reduce<Record<Priority, NotificationEntity[]>>(
    (acc, priority) => {
      acc[priority] = bannerNotifications
        .filter((n) => n.priority === priority)
        .sort((a, b) => b.createdAt - a.createdAt);
      return acc;
    },
    { critical: [], warning: [], info: [] },
  );

  const slots = PRIORITY_ORDER.filter((p) => byPriority[p].length > 0);

  const collapseLabel = [
    byPriority.critical.length > 0 ? `${priorityEmoji.critical}${byPriority.critical.length}` : '',
    byPriority.warning.length > 0 ? `${priorityEmoji.warning}${byPriority.warning.length}` : '',
    byPriority.info.length > 0 ? `${priorityEmoji.info}${byPriority.info.length}` : '',
  ]
    .filter(Boolean)
    .join('  ');

  return (
    <Box sx={{ width: '100%' }}>
      {/* Collapse bar */}
      <Box
        display="flex"
        alignItems="center"
        px={1}
        py={0.25}
        sx={{ bgcolor: 'grey.100', cursor: 'pointer', userSelect: 'none' }}
        onClick={toggleCollapsed}
      >
        <Typography variant="caption" sx={{ flex: 1 }}>
          {collapseLabel}
        </Typography>
        <IconButton
          size="small"
          aria-label={
            isBannerCollapsed
              ? t('notifications.banner.expand')
              : t('notifications.banner.collapse')
          }
        >
          {isBannerCollapsed ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ExpandLessIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      {/* Banner rows */}
      <Collapse in={!isBannerCollapsed}>
        <Stack>
          {slots.map((priority) => {
            const notifications = byPriority[priority];
            const isExpanded = expandedSlots.has(priority);
            const overflowCount = notifications.length - 1;
            // Show all when expanded, otherwise only the most recent
            const visible = isExpanded ? notifications : notifications.slice(0, 1);

            return (
              <Box key={priority}>
                {visible.map((notification) => (
                  <BannerRow
                    key={notification.id}
                    notification={notification}
                    onDismiss={dismissBanner}
                  />
                ))}

                {overflowCount > 0 && (
                  <Box
                    display="flex"
                    alignItems="center"
                    px={2}
                    py={0.5}
                    gap={0.5}
                    sx={{
                      bgcolor: 'grey.50',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'grey.100' },
                    }}
                    onClick={() => {
                      toggleSlot(priority);
                    }}
                  >
                    <Typography variant="caption" color="primary.main" sx={{ flex: 1 }}>
                      {isExpanded
                        ? t('notifications.banner.showLess')
                        : `+${overflowCount} ${t('notifications.banner.more')}`}
                    </Typography>
                    {isExpanded ? (
                      <ExpandLessIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
};
